/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { Row } from '../../../layout/flexbox';
import { Select } from '../../../forms/elements/select/Select';
import { Option } from '../../../forms/elements/select/Option';
import * as Buttons from '../../../forms/elements/buttons';
import Tooltip from '../../../layout/tooltip/Tooltip';
import { REDEMPTION_MAX } from './module';
import * as R from 'ramda';
import { ROW_SPACING } from '../../../forms/styles';
import { MediumText, TOOLTIP } from '../../../styles/styles';
import { assoc } from '../../../language/module';
import { getResource } from '../../../language/resources/getResource';
import { IAppState } from '../../../core/redux/IAppState';
import { connect } from 'react-redux';


const RedeemOption = styled(Option)`
  ${MediumText};
` as any;

const ZeroDaysTooltip = styled('span')`
  ${TOOLTIP};
  text-align: center;
  width: 200px;
`;

type Props = {
  rootCss?: SerializedStyles;
  disable?: boolean;

  //The currently selectedItem number of redemptions to make when the form is submitted.
  redemptionQuantity?: number;

  //The maximum number of redemptions we can make for this guest.
  remainingDays: number;

  //Total Days used so that we can check and ensure the user cannot redeem the user to negative total days.
  totalDays: number;

  //Whether or not to include negative days for corrective redemptions.
  allowNegativeRedemption: boolean;

  onRedemptionSelectionChange?: (selectedAmount: number) => void;
  onSubmit: (redemptionQuantity: number) => void;
  resources?: Record<string, any>
}

type State = {
  redemptionQuantity: number;
}

/**
 * DaysToRedeemSelect: Renders a combination box of the possible redemption day options.  E.g. if 3 days are available, renders a box with 0, 1, 2, and 3 days.
 * If no days are available and is deemed invalid in any way based on data, it is set to a disabled state.
 * If remaining days available is null or below 0, it is set to a disabled state
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default connect(
  (state: IAppState, props: Props) => ({
    resources: state.locale,
  }))(class DaysToRedeemSelect extends React.PureComponent<Props, State> {
  public state = {
    redemptionQuantity: this.props.redemptionQuantity || 0,
  };


  public componentDidUpdate(prevProps, prevState) {
    // If our parent passed us a new redemption quantity, update to use it.
    if (this.props.redemptionQuantity !== prevProps.redemptionQuantity) {
      this.setState(prev => assoc('redemptionQuantity', this.props.redemptionQuantity!, prev));
    }
  }


  private onSelectionChange = event => {
    const newValue = R.path<number>([ 'target', 'value' ], event);

    if (!newValue) {
      return;
    }

    this.setState(prev => assoc('redemptionQuantity', +newValue, prev));

    if (this.props.onRedemptionSelectionChange) {
      this.props.onRedemptionSelectionChange(newValue);
    }
  };


  private onSubmit = () => {
    if (!this.state.redemptionQuantity) {
      return;
    }

    this.props.onSubmit(this.state.redemptionQuantity);
  };


  /**
   * Maps the maximum number of redemptions left to the guest into a list of options. We max out at the configured
   * REDEMPTION_MAX because some guests will have no limit which RTP will express as UNLIMITED_DAYS (see const at top).
   * We don't want to insert UNLIMITED_DAYS nodes into the DOM.
   */
  public generateRedemptionOptions = (
    usedDays: number,
    unusedDays: number,
    totalDays: number,
    allowNegative: boolean,
  ): React.ReactNode[] => {
    const allOptions = R.range(1, totalDays + 1);

    const endIndex = Math.max(Math.min(unusedDays, allOptions.length), allOptions.length - usedDays);

    const positiveOptions = allOptions.slice(0, endIndex);
    const negativeOptions = allowNegative ? R.range(Math.max(-2, -1 * usedDays), 0) : [];

    return negativeOptions.concat(positiveOptions).map(i => (
      <option key={i} value={i}>{getResource(this.props.resources, 'DAYS_REDEEM_NUMBER_DAYS', i)}</option>),
    );
  };


  public render() {
    const remainingDays = this.props.remainingDays > 0;
    const tooltip       = <ZeroDaysTooltip>{getResource(this.props.resources, 'DAYS_REDEEM_NO_DAYS')}</ZeroDaysTooltip>;

    return (
      <Row alignItems='center'
           rootCss={css`
             ${ROW_SPACING};
             ${this.props.rootCss};
           `}>
        <Select css={css`width: 150px;`}
                value={this.state.redemptionQuantity}
                onChange={this.onSelectionChange}
                title={getResource(this.props.resources, 'DAYS_REDEEM_SELECT_TITLE')}
                disabled={
                  this.props.disable
                  || this.props.remainingDays < 0
                  || (!remainingDays && !this.props.allowNegativeRedemption)
                }>
          <RedeemOption value={0} key={0}>{getResource(this.props.resources, 'DAYS_REDEEM_REDEEM')}</RedeemOption>
          {
            this.generateRedemptionOptions(
              this.props.totalDays - this.props.remainingDays,
              this.props.remainingDays,
              Math.min(REDEMPTION_MAX, this.props.totalDays),
              this.props.allowNegativeRedemption,
            )
          }
        </Select>

        <Tooltip tooltipContent={tooltip}
                 disable={this.props.allowNegativeRedemption || (!this.props.allowNegativeRedemption && remainingDays)}
                 activateOnHover={true}
                 side='top'>
          <Buttons.BigNeutral
            type='submit'
            title={getResource(this.props.resources, 'DAYS_REDEEM_SELECT_TITLE')}
            onClick={this.onSubmit}
            disabled={
              this.props.disable || this.state.redemptionQuantity === 0
            }>{getResource(this.props.resources, 'DAYS_REDEEM_BUTTON')}</Buttons.BigNeutral>
        </Tooltip>
      </Row>
    );
  }
});
